<template>
	<ol
		ref="pinListRef"
		tabindex="-1"
		class="form-tree form-tree--pinned no-list-style select-none"
		role="tree"
		:class="{ 'mb-4': sortedPinTreeData.length }"
	>
		<FormTreeNode
			v-for="pin in sortedPinTreeData"
			:key="pin.id"
			:node="pin"
			:search-query="searchQuery"
			:hidden-items="hiddenItems"
			:selected-nodes="selectedNodes"
			:depth="1"
			:is-draggable="sortableInitialized"
			:organization-code="globalStore.organization?.Code"
			:style="{ '--node-padding': '14px' }"
			:is-reordering="isReordering"
			tree-mode="pinned"
			class="pinned-item"
			@select="$emit('select', $event)"
			@rename="$emit('rename', $event)"
			@unpin="handleUnpin"
			@matches-query="matchesQuery"
		/>
	</ol>
</template>

  <script lang="ts">
	import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, type PropType, inject } from 'vue';
	import { Sortable } from '@shopify/draggable';
	import type { SortableStopEvent } from '@shopify/draggable';
	import { reorderUserPins } from '@cognitoforms/api/services/user-pin-service';
	import { type ITreeNode } from './FormTree.vue';
	import type { GlobalStore } from 'src/stores/global-store';
	import type { HiddenItemOptions } from './FormTreeContainer.vue';
	import { navStateKey } from 'src/utilities/injection-keys';
	import { RenamePin, useEventBus } from 'src/views/admin/utils/useEventBus';

	export default defineComponent({
		name: 'CUserPins',
		components: {
			FormTreeNode: () => import('./FormTreeNode.vue')
		},
		props: {
			globalStore: {
				type: Object as PropType<GlobalStore>,
				required: true
			},
			userPins: {
				type: Array as PropType<ITreeNode[]>,
				required: true
			},
			searchQuery: {
				type: String,
				required: false,
				default: ''
			},
			hiddenItems: {
				type: Object as PropType<HiddenItemOptions>,
				default: () => ({} as HiddenItemOptions)
			},
			selectedNodes: {
				type: Array as PropType<string[]>,
				required: false,
				default: () => []
			}
		},
		emits: ['select', 'rename', 'has-matches'],
		setup(props, { emit }) {
			const isReordering = ref(false);

			const sortedPinTreeData = computed(() => {
				const pinNodes: ITreeNode[] = [];
				if (!props.globalStore.userPins)
					return pinNodes;

				props.userPins.forEach((p) => {
					const formId = p.id.split('-')[0];
					const effectivePinId = p.id === `${formId}-0` ? `${formId}-1` : p.id;
					const storedPin = props.globalStore.userPins.find((pin) => pin.Id === effectivePinId);
					if (!storedPin)
						return;

					pinNodes.push({
						...p,
						type: p.type,
						name: storedPin.Name ?? p.name // Defaults to view name if pin is not renamed
					});
				});

				// Sort pins by the order they are stored in globalStore
				const storedPinIds = props.globalStore.userPins.map((p) => p.Id);
				const sortedPins: ITreeNode[] = [];
				storedPinIds.forEach((id) => {
					const correspondingPin = pinNodes.find(
						(node) => node.id === props.globalStore.getEffectivePin(id)
					);
					// If not found, means pinned view is inaccessible to user
					if (correspondingPin) sortedPins.push(correspondingPin);
				});

				return sortedPins;
			});

			const handleUnpin = (viewId: string) => {
				props.globalStore.unpinItem(viewId);
			};

			// searching logic
			const hasMatches = ref(false);

			const matchesQuery = () => {
				if (!props.searchQuery)
					return;

				hasMatches.value = true;
				emit('has-matches');
			};

			watch(() => props.searchQuery, (query) => {
				if (!query)
					hasMatches.value = false;
			});

			// sorting logic
			let timeoutId: NodeJS.Timeout;
			let orderedIds: string[] = [];
			const pinListRef = ref<HTMLElement>(null);

			const initOrderedIds = () => {
				orderedIds = props.userPins.map(p => p.id);
			};

			const savePinOrder = () => {
				reorderUserPins(orderedIds);
				isReordering.value = false;
			};

			const handleBeforeUnload = () => {
				if (!timeoutId)
					return;

				clearTimeout(timeoutId);
				savePinOrder();
			};

			const onDragStart = () => {
				isReordering.value = true;
				if (timeoutId)
					clearTimeout(timeoutId);
			};

			const onDragStop = (event: SortableStopEvent) => {
				const { oldIndex, newIndex } = event;
				orderedIds.splice(newIndex, 0, orderedIds.splice(oldIndex, 1)[0]);
				timeoutId = setTimeout(savePinOrder, 3000);
			};

			let sortableInstance = null;
			const sortableInitialized = ref(false);

			const initDraggable = () => {
				if (!pinListRef.value) return;

				// Destroy existing instance if already initialized
				if (sortableInstance) {
					sortableInstance.destroy();
					sortableInstance = null;
				}

				sortableInstance = new Sortable(pinListRef.value, {
					draggable: '.pinned-item',
					distance: 0,
					handle: '.form-tree__node-name .drag-handle',
					mirror: {
						appendTo: pinListRef.value,
						constrainDimensions: true,
						xAxis: false
					}
				});

				sortableInitialized.value = true;

				sortableInstance.on('sortable:start', onDragStart);
				sortableInstance.on('sortable:stop', onDragStop);
			};

			// Function to remove the sortable instance
			const destroyDraggable = () => {
				if (sortableInstance) {
					sortableInstance.destroy();
					sortableInstance = null;
					sortableInitialized.value = false;
				}
			};

			watch(sortedPinTreeData, () => {
				if (isReordering.value)
					savePinOrder();
				initOrderedIds();
			});

			watch(() => sortedPinTreeData.value.length, () => {
				if (sortedPinTreeData.value.length > 1) {
					if (!sortableInitialized.value)
						initDraggable();
				}
				else
					destroyDraggable();
			});

			const navState = inject(navStateKey);
			const { on: onRenamePinEvent } = useEventBus(RenamePin);
			onRenamePinEvent(({ pinId }) => {
				navState.setNodeRenameState(pinId, 'pinned');
			});

			onMounted(() => {
				if (sortedPinTreeData.value.length > 1)
					initDraggable();
				initOrderedIds();
				window.addEventListener('beforeunload', handleBeforeUnload);
			});

			onBeforeUnmount(() => {
				window.removeEventListener('beforeunload', handleBeforeUnload);
			});

			return {
				pinListRef,
				sortedPinTreeData,
				handleUnpin,
				hasMatches,
				matchesQuery,
				initDraggable,
				sortableInitialized,
				isReordering
			};
		}
	});
</script>

<style lang="scss">
.admin--mobile .form-tree--pinned .draggable-mirror {
	display: none;
}

</style>